import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import EmailOtpVerification from "../components/EmailOtpVerification";

function EmailOtpVerificationLogin() {
  const location = useLocation<any>();
  const history = useHistory<any>();

  const urlSearchParams = new URLSearchParams(location.search);
  const emailParam = urlSearchParams.get('email');
  const redirectUrl = urlSearchParams.get('redirect_to');
  const oauthFlow = urlSearchParams.get('oauth_flow');
  const modeParam = urlSearchParams.get('mode'); // 'login' or 'login_otp'
  const passwordParam = urlSearchParams.get('password'); // Password for login mode
  const isOAuthFlow = oauthFlow === '1' || oauthFlow === 'true';
  const currentSearch = location.search;
  const { state } = location;
  const emailFromState = state?.email;
  const passwordFromState = state?.password;
  const modeFromState = state?.mode;

  // Initialize email from state or query params immediately
  const initialEmail = emailFromState || emailParam || "";
  const [email, setEmail] = useState<string>(initialEmail);
  const [password, setPassword] = useState<string>(passwordFromState || passwordParam || "");
  const [mode, setMode] = useState<'login' | 'login_otp'>(modeFromState || (modeParam as 'login' | 'login_otp') || 'login');
  const [autoSendOtp, setAutoSendOtp] = useState(false); // Don't auto-send, user must click button
  const [otpAlreadySent, setOtpAlreadySent] = useState(state?.otpAlreadySent || false);

  useEffect(() => {
    // Get email from state (from login redirect) or query params
    const emailToUse = emailFromState || emailParam;
    if (emailToUse && emailToUse !== email) {
      setEmail(emailToUse);
    }
    
    // If OTP was already sent (from "Send Sign-in Code" button), show OTP input directly
    if (state?.otpAlreadySent) {
      setOtpAlreadySent(true);
    }
    
    // Don't auto-send OTP - user needs to click "Send OTP" button (unless already sent)
  }, [emailParam, emailFromState, state]);

  // Redirect to login if no email after component mounts
  useEffect(() => {
    if (!email && !emailParam && !emailFromState) {
      const timer = setTimeout(() => {
        history.push({
          pathname: "/login",
          search: currentSearch,
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [email, emailParam, emailFromState, history, currentSearch]);

  const handleVerifySuccess = (response: any) => {
    // After OTP verification, check if refresh_token is returned (user is logged in)
    if (response?.data?.refresh_token) {
      // User is logged in, redirect to dashboard
      if (redirectUrl) {
        try {
          const redirectTo = new URL(redirectUrl, window.location.origin);
          window.location.href = redirectTo.href;
        } catch (e) {
          history.push({
            pathname: "/",
            state: { response: true, message: "Email verified and logged in successfully" },
          });
        }
      } else {
        history.push({
          pathname: "/",
          state: { response: true, message: "Email verified and logged in successfully" },
        });
      }
    } else {
      // OTP verified but no token - try password login for unverified email scenario
      if (mode === 'login' && password) {
        // This should not happen normally as verify-otp should return token
        // But handle it gracefully
        history.push({
          pathname: "/login",
          search: currentSearch,
          state: {
            response: true,
            message: "Email verified successfully. Please login again.",
            verifiedEmail: email
          },
        });
      } else {
        // For login_otp mode, should have token
        history.push({
          pathname: "/login",
          search: currentSearch,
          state: {
            response: true,
            message: "Email verified successfully. Please login to continue.",
            verifiedEmail: email
          },
        });
      }
    }
  };

  // Don't render if no email
  if (!email) {
    return null;
  }

  return (
    <RightBanner $isOAuthFlow={isOAuthFlow}>
      {!isOAuthFlow && <Text>Hey, Hello</Text>}
      <Heading $isOAuthFlow={isOAuthFlow}>
        {isOAuthFlow ? "Verify OTP" : "Verify Your Email OTP"}
      </Heading>
      {mode === 'login' && (
        <InfoText $isOAuthFlow={isOAuthFlow}>
          Your email <strong>{email}</strong> is not verified. Please verify it to continue logging in.
        </InfoText>
      )}
      {mode === 'login_otp' && (
        <InfoText $isOAuthFlow={isOAuthFlow}>
          We've sent a sign-in code to <strong>{email}</strong>. Please enter the code to sign in.
        </InfoText>
      )}
      <OTPContainer>
        <EmailOtpVerification
          email={email}
          mode={mode}
          password={password}
          autoSendOtp={autoSendOtp}
          otpAlreadySent={otpAlreadySent}
          isOAuthFlow={isOAuthFlow}
          redirectUrl={redirectUrl}
          onVerifySuccess={handleVerifySuccess}
          onEmailChange={(newEmail) => setEmail(newEmail)}
        />
      </OTPContainer>
    </RightBanner>
  );
}

export default EmailOtpVerificationLogin;

const RightBanner = styled.div<{ $isOAuthFlow?: boolean }>`
  padding: 40px 100px;
  width: 100%;
  @media (max-width: 576px) {
    padding: 0px 0px;
  }
`;

const Heading = styled.h1<{ $isOAuthFlow?: boolean }>`
  font-weight: 700;
  font-size: 24px;
  line-height: 36px;
  color: ${props => props.$isOAuthFlow ? '#333333' : '#073b5f'};
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

const Text = styled.p`
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: #93acbd;
  margin: 0;
  @media (max-width: 576px) {
    font-size: 12px;
  }
`;

const InfoText = styled.p<{ $isOAuthFlow?: boolean }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: ${props => props.$isOAuthFlow ? '#666666' : '#93acbd'};
  margin: 16px 0 24px 0;
  text-align: center;
  @media (max-width: 576px) {
    font-size: 13px;
    margin: 12px 0 20px 0;
  }
`;

const OTPContainer = styled.div`
  width: 50%;
  margin: 40px auto;
  @media (max-width: 576px) {
    width: 100%;
  }
`;
